import { B2BProducts } from '@stytch/core/public';
import { StytchB2B } from '@stytch/react/b2b';
import {
  AuthFlowType,
  StytchEvent,
  StytchEventType,
  StytchSDKUIError,
} from '@stytch/vanilla-js';
import React, { memo, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useStyles } from 'uinix-ui';

import { StytchEmailTemplate } from '~/eds/src/enums/LoginPageType';

import { isDev } from '../../../../dev/utils/isDev';
import { Box } from '../../components/Box/Box';
import { Button } from '../../components/Button/Button';
import { ContentContainer } from '../../components/ContentContainer/ContentContainer';
import { Divider } from '../../components/Divider/Divider';
import { EmailInput } from '../../components/EmailInput/EmailInput';
import { FormField } from '../../components/FormField/FormField';
import { Layout } from '../../components/Layout/Layout';
import { Logo } from '../../components/Logo/Logo';
import { Markdown } from '../../components/Markdown/Markdown';
import { PasswordInput } from '../../components/PasswordInput/PasswordInput';
import { StatusMessage } from '../../components/StatusMessage/StatusMessage';
import { Text } from '../../components/Text/Text';
import { TextInput } from '../../components/TextInput/TextInput';
import loginForgotPasswordImageSrc from '../../tokens/images/login-forgot-password.png';
import loginSignInImageSrc from '../../tokens/images/login-sign-in.png';
import loginWorkspaceImageSrc from '../../tokens/images/login-workspace.png';
import { Nullable } from '../../types/Nullable';
import { Queryable } from '../../types/Queryable';
import { testIsEmailValid } from '../../utils/testIsEmailValid';
import { Account, Sso, Stage } from './types';
import { testCanLogin } from './utils';

interface Props {
  /** Query when workspace is changed */
  queryChangeWorkspace: Queryable<(workspace: string) => void>;
  /** Query when logging in with account */
  queryLoginAccount: Queryable<(account: Account) => void>;
  /** Query when logging in with SSO provider */
  queryLoginSso: Queryable<(sso: Sso) => void>;
  /** Query when password reset is sent */
  queryResetPassword: Queryable<
    ({ email, workspace }: { email: string; workspace: string }) => void
  >;
  /** Flag to determine if the user can use the rolodex and stytch login component */
  enableRolodex?: boolean;
  /** Flag to determine if the user should login through OIDC */
  enableOidc?: boolean;
  /** Callback when the user login successfully using the rolodex */
  onRolodexLoginComplete?: () => void;
  /** Callback when returning to sign in stage */
  onBackToSignIn: () => void;
  /** Callback to resolve login sso error */
  onResolveLoginSsoError: () => void;
  /** Specify a valid SSO provider */
  sso: Nullable<Sso>;
  /** Renders only SSO login elements */
  isSsoOnly?: boolean;
  /** Controlled stage */
  stage?: Stage;
  /** Customize the welcome banner */
  welcomeBanner?: {
    banner?: React.ReactNode;
    bannerImageBackgroundSize?: string;
    bannerImagePosition?: string;
    bannerImageSrc?: string;
    padding?: number;
  };
  /** Controlled workspace */
  workspace?: string;
}

export const LoginPage = memo(
  ({
    isSsoOnly,
    queryChangeWorkspace,
    queryLoginAccount,
    queryLoginSso,
    queryResetPassword,
    enableRolodex = false,
    enableOidc = false,
    onRolodexLoginComplete,
    onBackToSignIn,
    onResolveLoginSsoError,
    sso,
    stage: initialStage = 'workspace',
    welcomeBanner,
    workspace: initialWorkspace = '',
  }: Props) => {
    const intl = useIntl();
    const [email, setEmail] = useState<Nullable<string>>('');
    const [password, setPassword] = useState<Nullable<string>>('');
    const [workspace, setWorkspace] =
      useState<Nullable<string>>(initialWorkspace);
    const [stage, setStage] = useState<Stage>(initialStage);

    const styles = useStyles();

    const changeWorkspaceText = intl.formatMessage({
      id: 'LoginPage.changeWorkspaceButton',
      defaultMessage: 'Change Workspace',
      description: 'Button to change workspace',
    });

    const emailLabel = intl.formatMessage({
      id: 'LoginPage.emailLabel',
      defaultMessage: 'Email',
      description: 'Label for email input',
    });

    const emailPlaceholder = intl.formatMessage({
      id: 'LoginPage.emailPlaceholder',
      defaultMessage: 'Type email address',
      description: 'Placeholder for email input',
    });

    const signInLabel = intl.formatMessage({
      id: 'LoginPage.signInButton',
      defaultMessage: 'Sign In',
      description: 'Button to sign in',
    });
    useEffect(() => setStage(initialStage), [initialStage]); // sync state from props

    useEffect(() => {
      if (
        enableRolodex &&
        !enableOidc &&
        workspace &&
        stage === 'login' &&
        !window.location.pathname.includes(`${workspace}/login`)
      ) {
        window.history.replaceState(null, '', `${workspace}/login`);
      }
    }, [workspace, stage, enableRolodex, enableOidc]);

    const [onChangeWorkspace, { isFetching: isLoadingChangeWorkspace }] =
      queryChangeWorkspace;
    const [
      onLoginAccount,
      { isFetching: isLoadingLoginAccount, error: errorLoginAccount },
    ] = queryLoginAccount;
    const [
      onLoginSso,
      { error: errorLoginSso, isFetching: isLoadingLoginSso },
    ] = queryLoginSso;
    const [
      onResetPassword,
      { isFetching: isLoadingResetPassword, isSuccess: isSuccessResetPassword },
    ] = queryResetPassword;

    const stytchConfigs = {
      config: {
        authFlowType: AuthFlowType.Organization,
        products: [B2BProducts.passwords],
        passwordOptions: {
          loginRedirectURL: '',
          resetPasswordRedirectURL: '',
          resetPasswordTemplateId: StytchEmailTemplate.ResetPassword,
          resetPasswordExpirationMinutes: 120,
        },
        sessionOptions: {
          sessionDurationMinutes: 60 * 24,
        },
      },
      styles: {
        container: { width: '100%' },
        fontFamily: 'Arial, "Helvetica New", Helvetica, sans-serif',
      },
      callbacks: {
        onEvent: (event: StytchEvent) => {
          if (event.type === StytchEventType.AuthenticateFlowComplete) {
            onRolodexLoginComplete?.();
          }
        },
        onError: (_error: StytchSDKUIError) => {
          // TODO: handle error later
        },
      },
    };

    const resetLogin = () => {
      setEmail('');
      setPassword('');
    };

    let title;
    let description;
    let contents;
    let banner;
    let bannerAlign;
    let bannerImageBackgroundSize;
    let bannerImageSrc;
    let bannerImagePosition;
    let handleSubmit: () => void;
    switch (stage) {
      case 'login': {
        title = enableRolodex
          ? ''
          : intl.formatMessage({
              id: 'LoginPage.loginTitle',
              defaultMessage: 'Sign in to your Workspace',
              description: 'Title for sign in to workspace page',
            });
        description = enableRolodex ? null : (
          <>
            {intl.formatMessage({
              id: 'LoginPage.loginDescription',
              defaultMessage:
                'Enter your credentials to sign into the workspace: ',
              description: 'Prompt to enter workspace URL',
            })}
            <strong> {workspace}</strong>
          </>
        );
        contents = enableRolodex ? (
          enableOidc ? (
            // OIDC redirects user to the OIDC provider to login
            <ContentContainer loadingContent={{ isLoading: true }} />
          ) : (
            <>
              <StytchB2B
                config={stytchConfigs.config}
                styles={stytchConfigs.styles}
                callbacks={stytchConfigs.callbacks}
              />
              <Button
                text={changeWorkspaceText}
                theme="dark"
                variant="action"
                onClick={() => {
                  resetLogin();
                  setWorkspace('');
                  setStage('workspace');
                }}
              />
            </>
          )
        ) : (
          <>
            {sso && (
              <Layout direction="column" spacing={8}>
                {errorLoginSso && (
                  <StatusMessage
                    action={{
                      icon: 'clipboard',
                      text: intl.formatMessage({
                        id: 'LoginPage.copyButton',
                        defaultMessage: 'Copy',
                        description: 'Copy button for error message',
                      }),
                      onClick: onResolveLoginSsoError,
                    }}
                    status="danger"
                    message={errorLoginSso}
                  />
                )}
                <Button
                  isLoading={isLoadingLoginSso}
                  text={intl.formatMessage(
                    {
                      id: 'LoginPage.signInWithSsoButton',
                      defaultMessage: 'Sign In with {ssoName}',
                      description:
                        'Button to sign in with SSO provider. Example: ssoName = "Google"',
                    },
                    { ssoName: sso.name },
                  )}
                  onClick={() => {
                    resetLogin();
                    onLoginSso(sso);
                  }}
                />
                {isSsoOnly ? null : (
                  <Divider
                    flex="none"
                    label={intl.formatMessage({
                      id: 'LoginPage.orDivider',
                      defaultMessage: 'or',
                      description: 'Divider label for alternative login',
                    })}
                    theme="dark"
                  />
                )}
              </Layout>
            )}
            {errorLoginAccount && (
              <StatusMessage status="danger" message={errorLoginAccount} />
            )}
            {isSsoOnly ? null : (
              <>
                <FormField<string, false>
                  input={isDev ? TextInput : EmailInput}
                  label={emailLabel}
                  name="email"
                  placeholder={emailPlaceholder}
                  theme="dark"
                  value={email}
                  onChange={setEmail}
                />
                <FormField<string, false>
                  input={PasswordInput}
                  label={intl.formatMessage({
                    id: 'LoginPage.passwordLabel',
                    defaultMessage: 'Password',
                    description: 'Label for password input',
                  })}
                  name="password"
                  placeholder={intl.formatMessage({
                    id: 'LoginPage.passwordPlaceholder',
                    defaultMessage: 'Type password',
                    description: 'Placeholder for password input',
                  })}
                  theme="dark"
                  value={password}
                  width="100%"
                  onChange={setPassword}
                />

                <Button
                  disabled={!testCanLogin({ email, password })}
                  isLoading={isLoadingLoginAccount}
                  text={signInLabel}
                  type="submit"
                  variant="primary"
                />
                <Button
                  text={changeWorkspaceText}
                  theme="dark"
                  variant="action"
                  onClick={() => {
                    resetLogin();
                    setWorkspace('');
                    setStage('workspace');
                  }}
                />
                <Button
                  text={intl.formatMessage({
                    id: 'LoginPage.forgotPasswordButton',
                    defaultMessage: 'Forgot Password',
                    description: 'Button to reset password',
                  })}
                  theme="dark"
                  variant="action"
                  onClick={() => {
                    resetLogin();
                    setStage('reset-password');
                  }}
                />
              </>
            )}
          </>
        );
        banner = welcomeBanner?.banner ?? (
          <>
            <Box as="blockquote" m={0}>
              {intl.formatMessage({
                id: 'LoginPage.bannerQuote',
                defaultMessage:
                  'With Evisort, we spend our time doing the work that matters most while still keeping our promises to our customers.',
                description: 'Banner quote for login page',
              })}
            </Box>
            <Text as="cite" color="inverse.text.secondary" fontStyle="normal">
              <strong>Jonathan</strong> Sickler AE2S
            </Text>
          </>
        );
        bannerAlign = 'flex-end';
        bannerImageBackgroundSize = welcomeBanner?.bannerImageBackgroundSize;
        bannerImagePosition = welcomeBanner?.bannerImagePosition ?? 'top';
        bannerImageSrc = welcomeBanner?.bannerImageSrc ?? loginSignInImageSrc;
        handleSubmit = () => {
          if (email && password) {
            onLoginAccount({ email, password });
          }
        };
        break;
      }
      case 'reset-password': {
        title = isSuccessResetPassword
          ? intl.formatMessage({
              id: 'LoginPage.checkInboxTitle',
              defaultMessage: 'Check your Inbox',
              description: 'Title for check inbox after password reset',
            })
          : intl.formatMessage({
              id: 'LoginPage.resetPasswordTitle',
              defaultMessage: 'Reset your Password',
              description: 'Title for reset password page',
            });
        description = isSuccessResetPassword
          ? null
          : intl.formatMessage({
              id: 'LoginPage.resetPasswordDescription',
              defaultMessage:
                'Enter your email address to reset your password. You may need to check your spam folder or unblock evisort@evisort.com.',
              description: 'Description for reset password page',
            });
        const backToSignIn = (
          <Button
            text={signInLabel}
            theme="dark"
            variant="action"
            onClick={() => {
              setStage('login');
              setEmail('');
              onBackToSignIn();
            }}
          />
        );
        if (isSuccessResetPassword) {
          contents = (
            <>
              <StatusMessage
                status="success"
                message={intl.formatMessage({
                  id: 'LoginPage.resetLinkSentMessage',
                  defaultMessage:
                    'You will receive a reset link shortly. You may need to check your spam folder or unblock evisort@evisort.com.',
                  description:
                    'Message shown after sending password reset link',
                })}
              />
              {backToSignIn}
            </>
          );
        } else {
          contents = (
            <>
              <FormField<string, false>
                input={EmailInput}
                label={emailLabel}
                name="email"
                placeholder={emailPlaceholder}
                theme="dark"
                value={email}
                onChange={setEmail}
              />
              <Button
                disabled={!testIsEmailValid(email)}
                isLoading={isLoadingResetPassword}
                text={intl.formatMessage({
                  id: 'LoginPage.sendPasswordResetLinkButton',
                  defaultMessage: 'Send Password Reset Link',
                  description: 'Button to send password reset link',
                })}
                type="submit"
                variant="primary"
              />
              {backToSignIn}
            </>
          );
        }
        banner = (
          <Markdown
            color="inverse.text.primary"
            variant="banner"
            whiteSpace="pre-wrap"
            text={intl.formatMessage({
              id: 'LoginPage.missingKeysBanner',
              defaultMessage:
                "Missing house keys?<br/>Let's help you get back in…",
              description: 'Banner for missing keys on reset password page',
            })}
          />
        );

        bannerAlign = 'flex-start';
        bannerImagePosition = 'top';
        bannerImageSrc = loginForgotPasswordImageSrc;
        handleSubmit = () => {
          if (email && workspace) {
            onResetPassword({ email, workspace });
          }
        };
        break;
      }
      case 'workspace':
      default: {
        title = intl.formatMessage({
          id: 'LoginPage.workspaceTitle',
          defaultMessage: 'Sign in to your Workspace',
          description: 'Title for sign in to workspace page',
        });
        description = intl.formatMessage({
          id: 'LoginPage.workspaceDescription',
          defaultMessage: "Enter your workspace's Evisort URL.",
          description: 'Prompt to enter workspace URL',
        });
        contents = (
          <>
            <Layout align="center" as="label" htmlFor="workspace">
              <Text color="inverse.text.primary">clients.evisort.com/</Text>
              <TextInput
                name="workspace"
                placeholder={intl.formatMessage({
                  id: 'LoginPage.workspacePlaceholder',
                  defaultMessage: 'your-workspace',
                  description: 'Placeholder for workspace input',
                })}
                value={workspace}
                width="auto"
                onChange={setWorkspace}
              />
            </Layout>
            <Button
              disabled={!workspace}
              isLoading={isLoadingChangeWorkspace}
              text={intl.formatMessage({
                id: 'LoginPage.continueButton',
                defaultMessage: 'Continue',
                description: 'Button to continue after entering workspace',
              })}
              type="submit"
              variant="primary"
            />
          </>
        );
        banner = (
          <Markdown
            color="inverse.text.primary"
            variant="banner"
            whiteSpace="pre-wrap"
            text={intl.formatMessage({
              id: 'LoginPage.newWorkspaceBanner',
              defaultMessage: "New workspace…<br/>Who's this?",
              description: 'Banner for new workspace',
            })}
          />
        );
        bannerAlign = 'flex-start';
        bannerImagePosition = 'bottom';
        bannerImageSrc = loginWorkspaceImageSrc;
        handleSubmit = () => {
          if (workspace) {
            onChangeWorkspace(workspace);
            setStage('login');
          }
        };
        break;
      }
    }

    const componentStyles = {
      bannerImage: {
        alignItems: bannerAlign,
        backgroundImage: `url(${bannerImageSrc})`,
        backgroundOrigin: 'content-box',
        backgroundPosition: `center ${bannerImagePosition}`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: bannerImageBackgroundSize,
      },
      form: {
        borderBottomLeftRadius: 'l',
        borderBottomRightRadius: ['l', 'unset'],
        borderTopLeftRadius: 'l',
        borderTopRightRadius: ['l', 'unset'],
      },
    };

    return (
      <Layout minH="container.min-height" justify="center" w="100%">
        <Layout
          align="stretch"
          aria-label={stage}
          as="form"
          bg="inverse.background.quiet"
          direction="column"
          flex="none"
          p={8}
          spacing={4}
          styles={componentStyles.form}
          w="form.width.m"
          onSubmit={(event: Event) => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <a href="/" title="Evisort login page">
            <Logo theme="dark" />
          </a>
          <Text as="h1" color="inverse.text.primary" variant="title">
            {title}
          </Text>
          <Text color="inverse.text.primary">{description}</Text>
          {contents}
        </Layout>
        {!isSsoOnly && (
          <Box
            bg="inverse.background"
            display={['none', 'flex']}
            flex="auto"
            as="aside"
            p={welcomeBanner?.padding ?? 16}
            position="relative"
            styles={[
              styles.borderRadius.containerRight,
              componentStyles.bannerImage,
            ]}
          >
            <Text
              color="inverse.text.primary"
              variant="banner"
              whiteSpace="pre-wrap"
            >
              {banner}
            </Text>
          </Box>
        )}
      </Layout>
    );
  },
);
